import { SettingsStoreIdentity } from "./settingsStore";
import { Observable } from "core/Misc/observable";
import { DarkTheme, LightTheme } from "../themes/babylonTheme";
/**
 * The setting descriptor for persisting the theme mode preference.
 */
export const ThemeModeSettingDescriptor = {
    key: "ThemeMode",
    defaultValue: "system",
};
/**
 * Resolves the current theme based on user preference and system settings.
 * Listens for changes to both the persisted theme mode and the OS-level dark mode preference.
 */
export class ThemeResolver {
    constructor(_settingsStore) {
        this._settingsStore = _settingsStore;
        this._darkModeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        this._onChanged = new Observable();
        this._onDarkModeMediaQueryChange = () => this._onChanged.notifyObservers();
        this._darkModeMediaQuery.addEventListener("change", this._onDarkModeMediaQueryChange);
        this._settingsStoreObserver = this._settingsStore.onChanged.add((key) => {
            if (key === ThemeModeSettingDescriptor.key) {
                this._onChanged.notifyObservers();
            }
        });
    }
    get onChanged() {
        return this._onChanged;
    }
    get mode() {
        return this._settingsStore.readSetting(ThemeModeSettingDescriptor);
    }
    set mode(value) {
        if (value !== this.mode) {
            this._settingsStore.writeSetting(ThemeModeSettingDescriptor, value);
        }
    }
    get isDark() {
        const themeMode = this.mode;
        return themeMode === "dark" || (themeMode === "system" && this._darkModeMediaQuery.matches);
    }
    toggle() {
        this.mode = this.isDark ? "light" : "dark";
    }
    dispose() {
        this._onChanged.clear();
        this._darkModeMediaQuery.removeEventListener("change", this._onDarkModeMediaQueryChange);
        this._settingsStoreObserver.remove();
    }
}
/**
 * The unique identity symbol for the theme service.
 */
export const ThemeServiceIdentity = Symbol("ThemeService");
export const ThemeServiceDefinition = {
    friendlyName: "Theme Service",
    produces: [ThemeServiceIdentity],
    consumes: [SettingsStoreIdentity],
    factory: (settingsStore) => {
        const themeResolver = new ThemeResolver(settingsStore);
        return Object.assign(themeResolver, {
            get theme() {
                return themeResolver.isDark ? DarkTheme : LightTheme;
            },
        });
    },
};
//# sourceMappingURL=themeService.js.map